import axios from "axios";
import { Country, Currency, UserInfo, UserOnboardingInfo } from "../types";
import { Language } from "../types/index";
import {
  CURRENCY_STORAGE_ITEM,
  DEFAULT_CURRENCY,
  DEFAULT_COUNTRY,
} from "../constant/common.constant";
import { COUNTRY_STORAGE_ITEM } from "../constant/common.constant";
import { trackLogout } from "../utils/analytics.utils";
import { getAuthHeader } from "../utils/AuthUtils";

const USER_API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export enum ROLES_ENUM {
  ADMIN = "admin",
  USER = "user",
  DESIGNER = "designer",
  DISTRIBUTOR = "distributor",
}

class AuthService {
  setLocalUser(currency: Currency, country: Country = DEFAULT_COUNTRY) {
    localStorage.setItem(CURRENCY_STORAGE_ITEM, currency ?? DEFAULT_CURRENCY);
    localStorage.setItem(COUNTRY_STORAGE_ITEM, country ?? DEFAULT_COUNTRY);
  }

  clearLocalUser() {
    localStorage.removeItem("userInfo");
    localStorage.removeItem("userId");
    localStorage.removeItem("userEmail");
    localStorage.removeItem(CURRENCY_STORAGE_ITEM);
    localStorage.removeItem(COUNTRY_STORAGE_ITEM);
  }

  login(credentials: {
    email: string;
    password: string;
    inviteToken?: string | null;
  }) {
    return axios.post(
      USER_API_BASE_URL + "/api/v1/user/login",
      credentials,
      this.getAuthHeader()
    );
  }

  resetPasswordLink(data: { email: string; language: Language }) {
    return axios.post(USER_API_BASE_URL + "/api/v1/user/reset_password", data);
  }

  updatePassword(password: string, token: string | undefined) {
    return axios.post(
      USER_API_BASE_URL + `/api/v1/user/update_password/${token}`,
      { password }
    );
  }

  signUp(credentials: UserOnboardingInfo) {
    return axios.post(
      USER_API_BASE_URL + "/api/v1/user/signup",
      credentials,
      this.getAuthHeader()
    );
  }

  userExists(email: string) {
    return axios.get(USER_API_BASE_URL + "/api/v1/user/exists/" + email);
  }

  updateUserLanguage(language: Language) {
    return axios.post(
      USER_API_BASE_URL + "/api/v1/user/language",
      {
        language,
      },
      this.getAuthHeader()
    );
  }

  updateUserCurrency(currency: Currency) {
    return axios.post(
      USER_API_BASE_URL + "/api/v1/user/currency",
      {
        currency,
      },
      this.getAuthHeader()
    );
  }

  async getUserImageUrl(): Promise<any> {
    try {
      return await axios.get(
        USER_API_BASE_URL + "/api/v1/user/profileImage",
        this.getAuthHeader()
      );
    } catch (error) {
      return undefined;
    }
  }

  async uploadUserProfileImage(imageData: string, fileName: string) {
    return axios.post(
      USER_API_BASE_URL + "/api/v1/user/profileImage",
      {
        imageData,
        fileName,
      },
      this.getAuthHeader()
    );
  }

  async removeUserProfileImage() {
    return axios.delete(
      USER_API_BASE_URL + "/api/v1/user/profileImage",
      this.getAuthHeader()
    );
  }

  async updateUserPassword(newPassword: string, oldPassword: string) {
    return axios.post(
      `${USER_API_BASE_URL}/api/v1/user/password`,
      { newPassword, oldPassword },
      getAuthHeader()
    );
  }

  async updateUserName(firstName?: string, lastName?: string) {
    const response = await axios.put(
      `${USER_API_BASE_URL}/api/v1/user/name`,
      { firstName, lastName },
      getAuthHeader()
    );
    return response.data;
  }

  async updateUserEmail(email: string) {
    return axios.put(
      `${USER_API_BASE_URL}/api/v1/user/email`,
      { email },
      getAuthHeader()
    );
  }

  async confirmEmailUpdate(token: string) {
    const response = await axios.post(
      `${USER_API_BASE_URL}/api/v1/user/confirm_email/${token}`,
      {},
      getAuthHeader()
    );
    return response.data;
  }

  getUserCountry() {
    return localStorage.getItem(COUNTRY_STORAGE_ITEM);
  }

  getUserCurrency() {
    return localStorage.getItem(CURRENCY_STORAGE_ITEM);
  }

  decorateUserInfo(userInfo: UserInfo | null) {
    if (userInfo) {
      userInfo.isAdmin = false;
      userInfo.isUser = false;
      userInfo.isDesigner = false;
      userInfo.country = (this.getUserCountry() ?? DEFAULT_COUNTRY) as Country;
      userInfo.currency = (this.getUserCurrency() ??
        DEFAULT_CURRENCY) as Currency;

      if (userInfo.roles) {
        if (
          userInfo.roles.some((roleObj) => roleObj.role === ROLES_ENUM.ADMIN)
        ) {
          userInfo.isAdmin = true;

          userInfo.canAddProject = true;
          userInfo.canRenameProject = true;
          userInfo.canDeleteProject = true;

          userInfo.canAddTeam = true;
          userInfo.canRenameTeam = true;
          userInfo.canDeleteTeam = true;

          userInfo.canAddDesign = true;
          userInfo.canRenameDesign = true;
          userInfo.canDeleteDesign = true;
          userInfo.canMoveDesign = true;
          userInfo.canUpdateDesignStyles = true;
          userInfo.canUpdateDesignColors = true;
          userInfo.canUpdateArtworkPosition = true;
          userInfo.canUpdateArtworkColor = true;
          userInfo.canAddArtwork = true;
          userInfo.canDeleteArtwork = true;
        }

        if (
          userInfo.roles.some((roleObj) => roleObj.role === ROLES_ENUM.DESIGNER)
        ) {
          userInfo.isDesigner = true;

          userInfo.canAddProject = true;
          userInfo.canRenameProject = true;
          userInfo.canDeleteProject = true;

          userInfo.canAddTeam = true;
          userInfo.canRenameTeam = true;
          userInfo.canDeleteTeam = true;

          userInfo.canAddDesign = true;
          userInfo.canRenameDesign = true;
          userInfo.canDeleteDesign = true;
          userInfo.canMoveDesign = true;
          userInfo.canUpdateDesignStyles = true;
          userInfo.canUpdateDesignColors = true;
          userInfo.canUpdateArtworkPosition = true;
          userInfo.canUpdateArtworkColor = true;
          userInfo.canAddArtwork = true;
          userInfo.canDeleteArtwork = true;
        }

        if (
          userInfo.roles.some((roleObj) => roleObj.role === ROLES_ENUM.USER) ||
          userInfo.roles.some(
            (roleObj) => roleObj.role === ROLES_ENUM.DISTRIBUTOR
          )
        ) {
          userInfo.isUser = true;

          userInfo.canAddProject = true;
          userInfo.canRenameProject = true;
          userInfo.canDeleteProject = true;

          userInfo.canAddTeam = true;
          userInfo.canRenameTeam = true;
          userInfo.canDeleteTeam = true;

          userInfo.canAddDesign = true;
          userInfo.canRenameDesign = true;
          userInfo.canDeleteDesign = true;
          userInfo.canMoveDesign = true;
          userInfo.canUpdateDesignStyles = true;
          userInfo.canUpdateDesignColors = true;
          userInfo.canUpdateArtworkPosition = true;
          userInfo.canUpdateArtworkColor = true;
          userInfo.canAddArtwork = true;
          userInfo.canDeleteArtwork = true;
        }
      } else {
        //Should never happen...
        userInfo.isUser = true;
      }
    }

    // console.debug(userInfo);
    return userInfo;
  }

  async getUserInfo() {
    try {
      const response = await axios.get<{ data: UserInfo }>(
        `${USER_API_BASE_URL}/api/v1/user/session`,
        this.getAuthHeader()
      );

      return this.decorateUserInfo(response.data.data);
    } catch (_error) {
      return null;
    }
  }

  getAuthHeader(contentType = "application/json") {
    return {
      headers: {
        "Content-Type": contentType,
      },
      withCredentials: true,
    };
  }

  async logOut() {
    //Segment Analytics
    trackLogout();

    //Tell the backend that user signed out
    try {
      await axios.post(
        USER_API_BASE_URL + "/api/v1/user/logout",
        {},
        this.getAuthHeader()
      );
    } catch (e) {
      console.error("Error trying to logout from backend server", e);
    }

    //Clear local
    this.clearLocalUser();

    return;
  }

  getAPIBaseUrl() {
    return USER_API_BASE_URL;
  }
}

const instance = new AuthService();
export default instance;
